"use client";

import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PencilIcon, PlusIcon, XIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { WalletFeeConfigItem } from "../api/wallet-fee-client";
import { useWalletFeeListQuery } from "../api/wallet-fee-queries";

const FEE_TYPE_OPTIONS = [
  { value: "all", label: "ປະເພດທັງໝົດ" },
  { value: "marketplace_commission", label: "ຄ່າຄອມ Marketplace" },
  { value: "withdraw_fee", label: "ຄ່າທຳນຽມຖອນເງິນ" },
  { value: "top_up_fee", label: "ຄ່າທຳນຽມເຕີມເງິນ" },
] as const;

const FEE_MODE_OPTIONS = [
  { value: "all", label: "ໂໝດທັງໝົດ" },
  { value: "percent", label: "ເປີເຊັນ" },
  { value: "flat", label: "ຈຳນວນຄົງທີ່" },
] as const;

const FEE_TYPE_LABELS: Record<string, string> = {
  marketplace_commission: "ຄ່າຄອມ Marketplace",
  withdraw_fee: "ຄ່າທຳນຽມຖອນເງິນ",
  top_up_fee: "ຄ່າທຳນຽມເຕີມເງິນ",
};

const FEE_MODE_LABELS: Record<string, string> = {
  percent: "ເປີເຊັນ",
  flat: "ຈຳນວນຄົງທີ່",
};

export function WalletFeesPage() {
  const nav = useNavigate({ from: "/app/wallet/fees" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/fees",
  }) as OffsetPageQueryDTO;

  const { data, isLoading } = useWalletFeeListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const feeTypeFilter =
    (findCondition(filters, "feeType")?.value as string) || "all";
  const feeModeFilter =
    (findCondition(filters, "feeMode")?.value as string) || "all";
  const hasFilter = feeTypeFilter !== "all" || feeModeFilter !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<WalletFeeConfigItem>[] = [
    createSortableColumn<WalletFeeConfigItem>("feeType", "ປະເພດ", {
      cell: ({ row }) => (
        <span className="text-sm">
          {FEE_TYPE_LABELS[row.original.feeType] ?? row.original.feeType}
        </span>
      ),
    }),
    createSortableColumn<WalletFeeConfigItem>("feeMode", "ໂໝດ", {
      cell: ({ row }) => (
        <span className="text-sm">
          {FEE_MODE_LABELS[row.original.feeMode] ?? row.original.feeMode}
        </span>
      ),
    }),
    {
      id: "value",
      header: "ຄ່າ",
      cell: ({ row }) => {
        const item = row.original;
        if (item.feeMode === "percent") {
          return (
            <span className="font-semibold tabular-nums">{item.value}%</span>
          );
        }
        return (
          <span className="font-semibold tabular-nums">
            {formatPrice(item.value, item.currency)}
          </span>
        );
      },
      size: 120,
    },
    {
      id: "currency",
      header: "ສະກຸນເງິນ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.currency}</span>
      ),
      size: 80,
    },
    {
      id: "minAmount",
      header: "ຂັ້ນຕ່ຳ",
      cell: ({ row }) =>
        row.original.minAmount != null
          ? formatPrice(row.original.minAmount, row.original.currency)
          : "—",
      size: 100,
    },
    {
      id: "maxAmount",
      header: "ຂັ້ນສູງ",
      cell: ({ row }) =>
        row.original.maxAmount != null
          ? formatPrice(row.original.maxAmount, row.original.currency)
          : "—",
      size: 100,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ເປີດໃຊ້ງານ" : "ປິດ"}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<WalletFeeConfigItem>("createdAt", "ວັນທີສ້າງ", {
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ແກ້ໄຂ",
              icon: <PencilIcon className="size-4" />,
              onClick: () =>
                nav({
                  to: "/app/wallet/fees/$id/edit",
                  params: { id: row.original.id },
                }),
            },
          ]}
        />
      ),
      size: 60,
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄ່າທຳນຽມກະເປົາ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">
                ຄ່າທຳນຽມກະເປົາ
              </h2>
              <p className="text-muted-foreground">
                ຈັດການກົດຄ່າທຳນຽມ (withdraw, top-up, marketplace commission)
              </p>
            </div>
            <Button
              onClick={() => nav({ to: "/app/wallet/fees/create" })}
              className="shrink-0"
            >
              <PlusIcon className="size-4" /> ສ້າງກົດຄ່າທຳນຽມ
            </Button>
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <SimpleSelect
                  value={feeTypeFilter}
                  onValueChange={(v) => handleFilterChange("feeType", v)}
                  placeholder="ປະເພດ"
                  className="h-8 w-full sm:w-52"
                  options={FEE_TYPE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={feeModeFilter}
                  onValueChange={(v) => handleFilterChange("feeMode", v)}
                  placeholder="ໂໝດ"
                  className="h-8 w-full sm:w-40"
                  options={FEE_MODE_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: { ...search, filters: undefined, offset: 0 },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="size-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<WalletFeeConfigItem, unknown>
            noDataMessage="ບໍ່ພົບກົດຄ່າທຳນຽມ"
            isLoading={isLoading}
            columns={columns}
            data={data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
